import { UnleashClient, type IMutableContext } from '@unleash/proxy-client-vue';

import config from '@/config';

/**
 * Get the Authorization header for the feature flag client.
 * @returns {string}
 */
export function getAuthHeader() {
  const key = 'access_token';
  const token = sessionStorage?.getItem(key) || localStorage?.getItem(key);
  return `Bearer ${token || ''}`;
}

/**
 * Custom fetch for the feature flag client.
 * It will inject the current authorization header for the user instead
 * of using an static value.
 * @param {string | URL} url
 * @param {RequestInit} init
 * @returns {Response}
 */
function customFetch(url, init) {
  init = init || {};
  init.headers = init.headers || {};
  init.headers.Authorization = getAuthHeader();
  init.headers['X-UI-AppName'] = config.ui_app_name;
  return fetch(url, init);
}

export function getFeatureFlagContext(orgId: string): IMutableContext {
  return { properties: { orgId } };
}

// Neutron evaluates the BFF flags against the same `warehouseId` property, so client and
// server always agree on whether a warehouse is on the BFF.
export function getFeatureFlagWarehouseContext(warehouseId: string): IMutableContext {
  return { properties: { warehouseId } };
}

const featureFlagConfig = {
  url: config.unleash_url,
  appName: config.unleash_app_name,
  clientKey: getAuthHeader(),
  environment: config.server_env === 'production' ? 'production' : 'development',
  fetch: customFetch,
  disableMetrics: true,
  usePOSTrequests: true
};

let client: UnleashClient | undefined;

export function createFeatureFlagClient(): UnleashClient {
  client ??= new UnleashClient(featureFlagConfig);
  return client;
}

// updateContext replaces every property, so keep the ones already applied (orgId and
// warehouseId can be set independently).
async function mergeFeatureFlagContext(featureFlagClient: UnleashClient, context: IMutableContext) {
  await featureFlagClient.updateContext({
    properties: { ...featureFlagClient.getContext().properties, ...context.properties }
  });
}

export async function setFeatureFlagOrgId(orgId?: string | null): Promise<void> {
  if (!client || !orgId || client.getContext().properties?.orgId === orgId) {
    return;
  }

  await mergeFeatureFlagContext(client, getFeatureFlagContext(orgId));
}

export async function setFeatureFlagWarehouseId(warehouseId?: string | null): Promise<void> {
  if (!client || !warehouseId || client.getContext().properties?.warehouseId === warehouseId) {
    return;
  }

  await mergeFeatureFlagContext(client, getFeatureFlagWarehouseContext(warehouseId));
}
